import React, { useState, useEffect } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { connect } from 'containers/store'

import { FieldSection } from 'components/ui/fieldset'
import { Input } from 'components/ui/input'
import { Select } from 'components/ui/select'

import { MediaField } from 'domains/media/components/media-field'

import { Media } from 'generated/mos/media'
import { VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { UpdateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues } from 'helpers/schema'
import { Status } from 'helpers/status'

import { actionCreators, GettyActionCreators } from '../../../../actions'

import { GettyStore } from '../../../../store'

import { appPageSchema, visitorInfoSchema } from '../../../../schemas/visitorInfo'

type FormValues = {
  pageType: VisitorInfoPage.AppPage.PageType;
  listTitle: string | undefined;
}

type ActionProps =
  Pick<GettyActionCreators, 'gettyUpdateVisitorInfoPageRequest'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly page: VisitorInfoPage.Entity | undefined;
  readonly setSubmit: (submit: () => void) => void;
  readonly setHasSubmitted: (submitted: boolean) => void;
  readonly setModified: (modifed: boolean | undefined) => void;
}

type Props = ActionProps & ConnectedProps & DirectProps

const AppPageUpdateView = (props: Props) => {
  const {
    visitorInfoUpdate,
    gettyUpdateVisitorInfoPageRequest,
    page,
    setSubmit,
    setHasSubmitted,
    setModified,
  } = props

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ iconMedia, setIconMedia ] = useState<Media.Ref | undefined>(undefined)

  const pageValues: Partial<VisitorInfoPage.AppPage.Entity & VisitorInfoPage.Entity> = pick({
    ...VisitorInfoPage.AppPage.defaults,
    ...VisitorInfoPage.defaults,
    ...page,
    ...(page && page.pageProperties) ? page.pageProperties: null
  }, 'pageType', 'listTitle')

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = page ?
    convertEntityToFormValues<VisitorInfoPage.AppPage.Entity & VisitorInfoPage.Entity | VisitorInfoPage.AppPage.Entity>(pageValues, appPageSchema) as any as FormValues:
    undefined
  const initialIconValue = page && appPageSchema.listIconRef.read(page.listIconRef) as any as Media.Ref | undefined

  if (page && initialFormValues && !formBag && visitorInfoUpdate.status === Status.Ready) {
    setFormBag(createFormBag(initialFormValues))
    setIconMedia(initialIconValue)
  }

  useEffect(() => {
    setSubmit(() => () => onSubmit())
  })

  // handle the form ui.
  const modified = initialFormValues && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialIconValue, iconMedia)
  )

  setModified(modified)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.listTitle || !values.listTitle.trim().length) {
      errors.listTitle = 'List title is required'
    }

    return errors
  }

  const onSubmit = () => {
    return formBag && page && submit(formBag, page)
  }

  const submit = (formBag: FormBag<FormValues>, page: VisitorInfoPage.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if(!bag.valid) {
      return false
    }

    const pageProperties = page && page.pageProperties as VisitorInfoPage.AppPage.Entity
    const { listTitle, pageType } = formBag.values

    setHasSubmitted(true)

    gettyUpdateVisitorInfoPageRequest({
      ...UpdateVisitorInfoPageRequest.defaults,
      visitorInfoPageRef: VisitorInfoPage.mustRef(page.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'listIconRef',
          'listTitle',
          'appPage',
        ]
      },
      listIconRef: visitorInfoSchema.listIconRef.write(iconMedia) || iconMedia,
      listTitle: visitorInfoSchema.listTitle.write(listTitle, page.listTitle) || listTitle,
      pageProperties: {
        ...VisitorInfoPage.AppPage.defaults,
        pageType: appPageSchema.pageType.write(pageType, pageProperties.pageType) || pageType,
      }
    })
    return false
  }

  const appPageTypeLabels = {
    PAGE_TYPE_SETTINGS: 'Settings',
    PAGE_TYPE_ABOUT_APP: 'About',
  }

  const appPageTypeOptions = Array.from(VisitorInfoPage.AppPage.PageTypeValues)
    .reduce((acc: ReadonlyArray<{ label: string; value: VisitorInfoPage.AppPage.PageType }>, curr: VisitorInfoPage.AppPage.PageType ) => {
      return curr !== 'PAGE_TYPE_UNSPECIFIED'
        ? [...acc, {
            label: appPageTypeLabels[curr],
            value: curr
          }]
        : acc
    }, [])


  return formBag && page && (
    <form noValidate onSubmit={(event) => { event.preventDefault() }}>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
          setSubmit(() => () => onSubmit())
          setModified(modified)
        }}
        validate={() => ({})}
      >
        <Select<FormValues, 'pageType'>
          label="Page Type"
          field="pageType"
          options={appPageTypeOptions}
          description="Select from built in app pages"
        />
        <FieldSection />
        <Input
          label="List title"
          field="listTitle"
          error={formBag.touched.listTitle ? formBag.errors.listTitle : undefined}
          description="The list title is the page's name in the navigation menu."
        />
        <MediaField
          kind="MEDIA_KIND_RASTER_IMAGE"
          mediaRef={iconMedia}
          onUpdate={(entity) => {
            setIconMedia(entity ? entity.ref : undefined)
            setSubmit(() => () => onSubmit())
          }}
          label="List icon"
        />
      </FormData>
    </form>
  )
}

export const AppPageUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorInfoUpdate'),
  {
    ...pick(actionCreators, 'gettyUpdateVisitorInfoPageRequest'),
  },
)(AppPageUpdateView)
